/**
 * Module-level token store.
 *
 * Stores the JWT received from the parent app via postMessage so both the
 * axios client (client.ts) and the raw fetch streaming client (chatService.ts)
 * can attach it to requests without needing React context.
 *
 * Also provides ensureFreshToken() which checks the JWT exp claim and
 * re-requests a token from the parent app if it's expired or about to expire.
 */

import { jwtDecode } from 'jwt-decode'

let token: string | null = null

export function setToken(t: string | null): void {
  token = t
}

export function getToken(): string | null {
  return token
}

/** Treat token as expired this many seconds before actual expiry. */
const EXPIRY_BUFFER_S = 30

/**
 * Check if the current token is expired or about to expire.
 */
export function isTokenExpired(): boolean {
  if (!token) return true
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token)
    if (typeof exp !== 'number') return true
    return Date.now() / 1000 >= exp - EXPIRY_BUFFER_S
  } catch {
    return true
  }
}

const TOKEN_REFRESH_TIMEOUT_MS = 3_000
let activeRefreshPromise: Promise<void> | null = null

/**
 * Ensure the token is fresh before making a request.
 * If expired or about to expire, sends nemo:ready to the parent app
 * to trigger a fresh nemo:auth. Concurrent calls piggyback on the
 * same in-flight refresh to avoid duplicate nemo:ready messages.
 * Resolves immediately if the token is still valid.
 */
export function ensureFreshToken(): Promise<void> {
  const authEnabled = import.meta.env.VITE_ENABLE_AUTH === 'true'
  const allowedOrigin = import.meta.env.VITE_PARENT_APP_ORIGIN ?? '*'

  if (!authEnabled || window.parent === window || !isTokenExpired()) {
    return Promise.resolve()
  }

  if (activeRefreshPromise) {
    return activeRefreshPromise
  }

  activeRefreshPromise = new Promise((resolve) => {
    const timeout = setTimeout(() => {
      window.removeEventListener('message', handler)
      activeRefreshPromise = null
      resolve()
    }, TOKEN_REFRESH_TIMEOUT_MS)

    function handler(event: MessageEvent) {
      if (allowedOrigin !== '*' && event.origin !== allowedOrigin) return
      if (
        event.data &&
        typeof event.data === 'object' &&
        event.data.type === 'nemo:auth' &&
        typeof event.data.token === 'string'
      ) {
        clearTimeout(timeout)
        window.removeEventListener('message', handler)
        setToken(event.data.token)
        activeRefreshPromise = null
        resolve()
      }
    }

    window.addEventListener('message', handler)
    window.parent.postMessage({ type: 'nemo:ready' }, allowedOrigin)
  })

  return activeRefreshPromise
}
